import React, { useMemo } from 'react';

/**
 * Block page for in-app browser environments (WeChat, QQ, Weibo, DingTalk)
 * where IndexedDB cannot be trusted to persist Reader data reliably.
 * Instead of letting the user enter Reader and hit cryptic IDB transaction
 * errors (see shejiwushi@qq.com, 2026-04-22), we show a clear instruction
 * to open the page in the system browser.
 *
 * Intentionally lightweight: no i18n import, no store, no global CSS deps —
 * this component must render before any of Reader's heavyweight initialisers
 * (AuthProvider, IDB open, etc.) run, because those are the things that
 * trigger the very errors this page exists to prevent.
 *
 * Simple zh/en toggle based on navigator.language — covers the primary
 * audience (Chinese users from WeChat) with an English fallback for others.
 */

function pickLang() {
  if (typeof navigator === 'undefined') return 'zh';
  const lang = (navigator.language || navigator.languages?.[0] || '').toLowerCase();
  return lang.startsWith('zh') ? 'zh' : 'en';
}

const COPY = {
  zh: {
    title: '请在浏览器中打开',
    subtitle: (app) => `检测到您正在${app || '应用内'}浏览器中访问 EaseVerse Reader。`,
    why: 'Reader 需要完整的本地存储才能保存您的书籍、阅读进度、笔记等数据。应用内置浏览器的存储空间受限，且可能在您不知情时清除数据。',
    stepsTitle: '如何在浏览器中打开：',
    steps: [
      '点击屏幕右上角的「⋯」或「•••」按钮',
      '选择「在浏览器中打开」或「在 Safari/Chrome 中打开」',
      '在弹出的浏览器中正常使用',
    ],
    copy: '复制链接',
    copied: '已复制',
  },
  en: {
    title: 'Please open in your browser',
    subtitle: (app) => `We detected you're opening EaseVerse Reader inside ${app ? app + "'s" : 'an'} in-app browser.`,
    why: 'Reader needs full local storage to save your books, reading progress, and notes. In-app browsers have limited storage and may wipe your data without warning.',
    stepsTitle: 'How to open in a real browser:',
    steps: [
      'Tap the «⋯» or «•••» menu (usually top-right)',
      'Choose "Open in Browser" / "Open in Safari" / "Open in Chrome"',
      'Continue in the browser that opens',
    ],
    copy: 'Copy link',
    copied: 'Copied',
  },
};

export default function InAppBrowserGate({ appName }) {
  const lang = useMemo(pickLang, []);
  const t = COPY[lang];
  const [copied, setCopied] = React.useState(false);

  const copyLink = async () => {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older in-app browsers without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* give up */ }
      document.body.removeChild(textarea);
    }
  };

  return (
    <div style={styles.root}>
      <div style={styles.card}>
        <div style={styles.icon} aria-hidden="true">📱</div>
        <h1 style={styles.title}>{t.title}</h1>
        <p style={styles.subtitle}>{t.subtitle(appName)}</p>
        <p style={styles.why}>{t.why}</p>

        <div style={styles.stepsBlock}>
          <div style={styles.stepsTitle}>{t.stepsTitle}</div>
          <ol style={styles.stepsList}>
            {t.steps.map((step, i) => (
              <li key={i} style={styles.stepItem}>{step}</li>
            ))}
          </ol>
        </div>

        <button
          type="button"
          onClick={copyLink}
          style={{ ...styles.button, ...(copied ? styles.buttonCopied : null) }}
        >
          {copied ? t.copied : t.copy}
        </button>
      </div>
    </div>
  );
}

// Inline styles keep the gate self-contained — no dependency on global.css
// (which may itself trigger the very init paths we're trying to avoid).
const styles = {
  root: {
    // 100dvh accounts for mobile browser chrome (address bar show/hide).
    // Baseline support since Chrome 108 / Safari 16.4 / Firefox 101 (2023).
    minHeight: '100dvh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1.5rem',
    background: 'linear-gradient(180deg, #faf7f2 0%, #f3ede3 100%)',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Source Sans 3', sans-serif",
    color: '#2a2520',
  },
  card: {
    maxWidth: '420px',
    width: '100%',
    background: '#fff',
    borderRadius: '16px',
    padding: '2rem 1.75rem',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
    textAlign: 'center',
  },
  icon: {
    fontSize: '3rem',
    lineHeight: 1,
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1.4rem',
    fontWeight: 600,
    margin: '0 0 0.5rem',
    color: '#1a1a1a',
  },
  subtitle: {
    fontSize: '0.95rem',
    color: '#555',
    margin: '0 0 1rem',
    lineHeight: 1.5,
  },
  why: {
    fontSize: '0.85rem',
    color: '#777',
    margin: '0 0 1.5rem',
    lineHeight: 1.6,
  },
  stepsBlock: {
    textAlign: 'left',
    background: '#f7f4ee',
    borderRadius: '10px',
    padding: '1rem 1.25rem',
    marginBottom: '1.5rem',
  },
  stepsTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#555',
    marginBottom: '0.5rem',
  },
  stepsList: {
    margin: 0,
    paddingLeft: '1.25rem',
    color: '#3a352e',
    fontSize: '0.9rem',
    lineHeight: 1.7,
  },
  stepItem: {
    marginBottom: '0.25rem',
  },
  button: {
    background: '#2a2520',
    color: '#fff',
    border: 'none',
    borderRadius: '10px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 500,
    cursor: 'pointer',
    minWidth: '180px',
    transition: 'background 0.15s',
  },
  buttonCopied: {
    background: '#3a7a3a',
  },
};
